import type {
  Dashboard,
  ReceivablesRow,
  ReportResult,
  RoutePerformanceRow,
  SalesByCustomerRow,
  SalesByProductRow,
} from '@fdt/shared';
import { api } from './client';

/** Both ends inclusive, as the API reads them. */
export interface Range {
  from: string;
  to: string;
}

function params(range: Range, extra: Record<string, string | undefined> = {}) {
  return Object.fromEntries(
    Object.entries({ ...range, ...extra }).filter(([, value]) => value !== undefined && value !== ''),
  );
}

export const reports = {
  async dashboard(range: Range): Promise<Dashboard> {
    const { data } = await api.get<Dashboard>('/reports/dashboard', { params: params(range) });
    return data;
  },

  async salesByProduct(
    range: Range,
    categoryId?: string,
  ): Promise<ReportResult<SalesByProductRow>> {
    const { data } = await api.get<ReportResult<SalesByProductRow>>('/reports/sales-by-product', {
      params: params(range, { categoryId }),
    });
    return data;
  },

  async salesByCustomer(range: Range): Promise<ReportResult<SalesByCustomerRow>> {
    const { data } = await api.get<ReportResult<SalesByCustomerRow>>('/reports/sales-by-customer', {
      params: params(range),
    });
    return data;
  },

  /** No window: a receivable is what is owed now (D120). */
  async receivables(debtorsOnly: boolean): Promise<ReportResult<ReceivablesRow>> {
    const { data } = await api.get<ReportResult<ReceivablesRow>>('/reports/receivables', {
      params: { debtorsOnly: String(debtorsOnly) },
    });
    return data;
  },

  async routePerformance(
    range: Range,
    filters: { userId?: string; status?: string } = {},
  ): Promise<ReportResult<RoutePerformanceRow>> {
    const { data } = await api.get<ReportResult<RoutePerformanceRow>>(
      '/reports/route-performance',
      { params: params(range, filters) },
    );
    return data;
  },
};
